// src/components/LoginModal.jsx
import React, { useState } from "react";
import { auth, db } from '../../firebase/config';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";

export default function LoginModal({ onClose }) {
  const [view, setView] = useState("login");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showMismatch, setShowMismatch] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [registerError, setRegisterError] = useState("");
  const [isShaking, setIsShaking] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
  });

  const [errors, setErrors] = useState({});


  // Shake animation function
  const triggerShake = () => {
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 1000);
  };

  const passwordChecks = {
    length: password.length >= 10,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
  };

  const sanitizationStr = /^[a-zA-Z0-9@._' -]*$/;

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    
    // Get form data
    const formData = new FormData(e.target);
    const email = formData.get("email");
    const password = formData.get("password");
    
    // Check if fields are filled
    if (!email || !password) {
      setLoginError("Please fill in all fields.");
      triggerShake();
      return;
    }

    try {
      await signInWithEmailAndPassword(auth, email, password);
      
      // Redirect to dashboard after successful login
      window.location.href = "https://dashboard.syncratik.com/";

    } catch(error) {
      triggerShake();

      if(error.code === 'auth/invalid-credential') {
        setLoginError("Invalid email or password. Please try again.");

      } else {
        setLoginError("Could not login successfully.");
      }
    }
    
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.email.trim()) newErrors.email = "This field is required";
    if (!formData.firstName.trim()) newErrors.firstName = "This field is required";
    if (!formData.lastName.trim()) newErrors.lastName = "This field is required";
    if (!password) newErrors.password = "This field is required";
    if (!confirmPassword) newErrors.confirmPassword = "This field is required";

    if (password && confirmPassword && password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    const sanitizationCheckList = ['email', 'firstName', 'lastName'];
    sanitizationCheckList.forEach((field) => {
      if(!sanitizationStr.test(formData[field])) {
        newErrors[field] = "This field cannot contain special characters";
      }
    });

    setErrors(newErrors);

    // Stop if errors
    if (Object.keys(newErrors).length > 0) return;

    const allValid =
      passwordChecks.length &&
      passwordChecks.upper &&
      passwordChecks.lower &&
      passwordChecks.number &&
      passwordChecks.special;

    if (!allValid) return;

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, formData.email, password);

      const newUser = userCredential.user;
      await sendEmailVerification(newUser);

      await setDoc(doc(db, "users", newUser.uid), {
        'first_name': formData.firstName,
        'last_name': formData.lastName,
        'email': newUser.email,
      });

      // Mock successful register
      window.location.href = "https://dashboard.syncratik.com/";

    } catch(error) {
      triggerShake();

      if(error.code === 'auth/email-already-in-use') {
        setRegisterError("Email already in use. Please try logging in.");

      } else if(error.code === 'auth/invalid-email') {
        setRegisterError("Invalid email. Please try again.");

      } else {
        setRegisterError("Could not register successfully.");
      }
    }

  };

  const handleConfirmChange = (e) => {
    const value = e.target.value;
    setConfirmPassword(value);
    setShowMismatch(value.length > 0 && value !== password);
    if (errors.confirmPassword) {
      setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm z-50"
      onClick={handleOverlayClick}
    >
      <div
        className="bg-white rounded-2xl shadow-xl p-8 w-96 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-600 hover:text-black"
        >
          ✕
        </button>

        {/* Title */}
        <h2 className="text-2xl font-bold mb-6 text-center">
          {view === "login" ? "Welcome" : "Get Started"}
        </h2>

        {/* Login View */}
        {view === "login" && (
          <>
            <form onSubmit={handleLogin} className="space-y-4">
              {loginError && (
                <div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded-lg">
                  {loginError}
                </div>
              )}
              <input
                type="email"
                name="email"
                placeholder="Email"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                required
              />
              <input
                type="password"
                name="password"
                placeholder="Password"
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                required
              />
              <button
                type="submit"
                className={`w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 transition ${isShaking ? 'animate-shake' : ''}`}
              >
                Log In
              </button>
            </form>

            <div className="text-center mt-4 text-sm text-gray-600">
              Don't have an account?{" "}
              <button
                onClick={() => setView("register")}
                className="text-purple-600 hover:underline"
              >
                Get Started
              </button>
            </div>
          </>
        )}

        {/* Register View */}
        {view === "register" && (
          <>
            <form onSubmit={handleRegister} className="space-y-4">
              {registerError && (
                <div className="text-red-600 text-sm text-center bg-red-50 p-2 rounded-lg">
                  {registerError}
                </div>
              )}
              {/* Email */}
              <div>
                <input
                  type="email"
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                {errors.email && (
                  <p className="text-red-600 text-sm">{errors.email}</p>
                )}
              </div>

              {/* First Name */}
              <div>
                <input
                  type="text"
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={(e) => handleChange("firstName", e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                {errors.firstName && (
                  <p className="text-red-600 text-sm">{errors.firstName}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <input
                  type="text"
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={(e) => handleChange("lastName", e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                {errors.lastName && (
                  <p className="text-red-600 text-sm">{errors.lastName}</p>
                )}
              </div>

              {/* Password */}
              <div>
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                {errors.password && (
                  <p className="text-red-600 text-sm">{errors.password}</p>
                )}
              </div>

              {/* Password Checklist */}
              <ul className="text-sm mt-2 space-y-1">
                <li className={passwordChecks.length ? "text-green-600" : "text-red-600"}>
                  • At least 10 characters
                </li>
                <li className={passwordChecks.upper ? "text-green-600" : "text-red-600"}>
                  • At least one uppercase letter (A-Z)
                </li>
                <li className={passwordChecks.lower ? "text-green-600" : "text-red-600"}>
                  • At least one lowercase letter (a-z)
                </li>
                <li className={passwordChecks.number ? "text-green-600" : "text-red-600"}>
                  • At least one number (0-9)
                </li>
                <li className={passwordChecks.special ? "text-green-600" : "text-red-600"}>
                  • At least one special character (!@#$%^&*)
                </li>
              </ul>

              {/* Confirm Password */}
              <div>
                <input
                  type="password"
                  placeholder="Re-enter Password"
                  value={confirmPassword}
                  onChange={handleConfirmChange}
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                />
                {errors.confirmPassword && (
                  <p className="text-red-600 text-sm">{errors.confirmPassword}</p>
                )}
                {showMismatch && !errors.confirmPassword && (
                  <p className="text-red-600 text-sm">Passwords do not match</p>
                )}
              </div>

              <button
                type="submit"
                className={`w-full bg-purple-600 text-white py-2 rounded-lg hover:bg-purple-700 transition ${isShaking ? 'animate-shake' : ''}`}
              >
                Register
              </button>
            </form>

            <div className="text-center mt-4 text-sm text-gray-600">
              Already have an account?{" "}
              <button
                onClick={() => setView("login")}
                className="text-purple-600 hover:underline"
              >
                Log In
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

